/**
 * Cumple — Detalle de payment + accion mark-paid (S4-E).
 *
 * Muestra metadata del payment, historial de events (append-only) y un
 * formulario para confirmar pago manual (Azul/CardNet/transferencia).
 * Mark-paid es transaccional en el backend (update payment + create
 * payment event + reactivar subscription si era past_due).
 */
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import { ApiError } from '../api/client.js';
import { adminBillingApi, type PaymentDetail } from '../api/admin-billing.js';

const NCF_RE = /^[A-Z]\d+$/;

export function AdminPaymentDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setBackTo } = useAppShell();
  const [payment, setPayment] = useState<PaymentDetail | null>(null);

  // Back button (BB-UX-01)
  useEffect(() => {
    setBackTo({ href: '/admin/billing', label: 'Billing' });
    return () => setBackTo(null);
  }, [setBackTo]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [externalRef, setExternalRef] = useState('');
  const [voucherUrl, setVoucherUrl] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    adminBillingApi
      .getPayment(id)
      .then(setPayment)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) setError('Payment no encontrado');
        else if (err instanceof ApiError) setError(`Error ${err.status}: ${err.code}`);
        else setError('Error de red');
      })
      .finally(() => setLoading(false));
  }, [id]);

  async function handleMarkPaid(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!id || !externalRef.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const trimmedVoucher = voucherUrl.trim();
      const trimmedNotes = notes.trim();
      const updated = await adminBillingApi.markPaid(id, {
        external_reference: externalRef.trim(),
        ...(trimmedVoucher ? { voucher_url: trimmedVoucher } : {}),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      });
      setPayment(updated);
      setExternalRef('');
      setVoucherUrl('');
      setNotes('');
    } catch (err) {
      if (err instanceof ApiError) setError(`${err.status}: ${err.code}`);
      else setError('Error al confirmar pago');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <p className="px-6 py-8 text-sm text-slate-500">Cargando payment...</p>;
  }
  if (error && !payment) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-8">
        <div role="alert" className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/billing')}
          className="mt-4 text-sm text-brand-700 underline"
        >
          ← Volver
        </button>
      </main>
    );
  }
  if (!payment) return <></>;

  const canMarkPaid = payment.status === 'pending';

  return (
    <main className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <header className="flex items-center justify-between">
        <Link to="/admin/billing" className="text-sm text-brand-700 underline">
          ← Volver
        </Link>
        <span className="text-xs text-slate-500 font-mono">{payment.id}</span>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
        <h1 className="text-xl font-semibold text-brand-900">{payment.tenant_name}</h1>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">Monto</dt>
          <dd className="font-medium">
            {payment.currency} {payment.amount.toFixed(2)}
          </dd>
          <dt className="text-slate-500">Plan</dt>
          <dd className="capitalize">{payment.subscription.plan_code}</dd>
          <dt className="text-slate-500">Subscription status</dt>
          <dd className="capitalize">{payment.subscription.status}</dd>
          <dt className="text-slate-500">Provider</dt>
          <dd>{payment.provider}</dd>
          <dt className="text-slate-500">Estado payment</dt>
          <dd className="capitalize">{payment.status}</dd>
          <dt className="text-slate-500">Vencimiento</dt>
          <dd>{new Date(payment.due_at).toLocaleDateString('es-DO')}</dd>
          {payment.paid_at && (
            <>
              <dt className="text-slate-500">Pagado</dt>
              <dd>{new Date(payment.paid_at).toLocaleString('es-DO')}</dd>
            </>
          )}
          {payment.marked_paid_by && (
            <>
              <dt className="text-slate-500">Confirmado por</dt>
              <dd>{payment.marked_paid_by}</dd>
            </>
          )}
          {payment.external_reference && (
            <>
              <dt className="text-slate-500">Ref externa</dt>
              <dd className="font-mono text-xs">{payment.external_reference}</dd>
            </>
          )}
        </dl>
        {payment.notes && (
          <div className="mt-3 rounded bg-slate-50 p-3 text-sm">
            <strong>Notas:</strong> {payment.notes}
          </div>
        )}
      </section>

      {canMarkPaid && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
          <h2 className="font-semibold text-emerald-900 mb-3">Confirmar pago manual</h2>
          <form
            onSubmit={(e) => {
              void handleMarkPaid(e);
            }}
            className="space-y-3"
          >
            <div>
              <label className="block text-sm font-medium mb-1">Referencia externa *</label>
              <input
                type="text"
                required
                value={externalRef}
                onChange={(e) => setExternalRef(e.target.value)}
                placeholder="Comprobante Azul/CardNet/transferencia"
                className="w-full rounded border-slate-300 px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">URL del voucher (opcional)</label>
              <input
                type="url"
                value={voucherUrl}
                onChange={(e) => setVoucherUrl(e.target.value)}
                placeholder="https://drive.mssprd.com/voucher.pdf"
                className="w-full rounded border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notas (opcional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                className="w-full rounded border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            {error && (
              <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !externalRef.trim()}
              className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700/90 disabled:bg-slate-300"
            >
              {submitting ? 'Confirmando...' : 'Confirmar pago'}
            </button>
          </form>
        </section>
      )}

      {payment.status === 'paid' && (
        <UploadInvoiceSection
          paymentId={payment.id}
          subscriptionId={payment.subscription.id}
          alreadyUploaded={payment.events.some(
            (e) =>
              typeof e.payload === 'object' &&
              e.payload !== null &&
              'kind' in e.payload &&
              e.payload.kind === 'invoice_uploaded',
          )}
          onUploaded={() => {
            if (!id) return;
            adminBillingApi
              .getPayment(id)
              .then(setPayment)
              .catch(() => undefined);
          }}
        />
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-semibold text-brand-900 mb-3">Historial (append-only)</h2>
        {payment.events.length === 0 ? (
          <p className="text-sm text-slate-500">Sin eventos.</p>
        ) : (
          <ol className="space-y-2 text-sm">
            {payment.events.map((e) => (
              <li key={e.id} className="border-l-2 border-slate-200 pl-3">
                <div className="font-medium">
                  {e.event_type} <span className="text-xs text-slate-500">· {e.actor_kind}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {new Date(e.created_at).toLocaleString('es-DO')}
                </div>
                <pre className="mt-1 text-xs bg-slate-50 rounded p-2 overflow-x-auto">
                  {JSON.stringify(e.payload, null, 2)}
                </pre>
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}

interface UploadInvoiceProps {
  paymentId: string;
  subscriptionId: string;
  alreadyUploaded: boolean;
  onUploaded: () => void;
}

function UploadInvoiceSection(props: UploadInvoiceProps): JSX.Element {
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [ncf, setNcf] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  function validate(): string | null {
    try {
      new URL(invoiceUrl.trim());
    } catch {
      return 'URL invalida';
    }
    if (ncf.trim() && !NCF_RE.test(ncf.trim()))
      return 'NCF debe ser una letra mayuscula + digitos (ej. B0100000001)';
    return null;
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const trimmedNcf = ncf.trim();
      const trimmedNotes = notes.trim();
      const result = await adminBillingApi.uploadInvoice(props.subscriptionId, {
        payment_id: props.paymentId,
        invoice_pdf_url: invoiceUrl.trim(),
        ...(trimmedNcf ? { ncf: trimmedNcf } : {}),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      });
      setSuccessMsg(result.message);
      setInvoiceUrl('');
      setNcf('');
      setNotes('');
      props.onUploaded();
    } catch (err) {
      if (err instanceof ApiError) setError(`${err.status}: ${err.code}`);
      else setError('Error al subir factura');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-2xl border border-blue-200 bg-blue-50/40 p-6">
      <h2 className="font-semibold text-blue-900 mb-1">Factura DGII manual</h2>
      <p className="text-xs text-slate-600 mb-4">
        Sube la URL del PDF generado fuera del sistema. Se asocia al pago como evento append-only en
        el audit log (no se duplica si subes una segunda).
      </p>

      {props.alreadyUploaded && !successMsg && (
        <div className="mb-3 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Este pago ya tiene una factura asociada. Subir otra crea un nuevo evento.
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-3"
      >
        <div>
          <label htmlFor="invoice_url" className="block text-sm font-medium mb-1">
            URL del PDF *
          </label>
          <input
            id="invoice_url"
            type="url"
            required
            value={invoiceUrl}
            onChange={(e) => setInvoiceUrl(e.target.value)}
            placeholder="https://drive.mssprd.com/factura-A0001.pdf"
            className="w-full rounded border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="ncf" className="block text-sm font-medium mb-1">
            NCF (opcional)
          </label>
          <input
            id="ncf"
            type="text"
            value={ncf}
            onChange={(e) => setNcf(e.target.value.toUpperCase())}
            placeholder="B0100000001"
            pattern="[A-Z]\d+"
            className="w-full rounded border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </div>
        <div>
          <label htmlFor="invoice_notes" className="block text-sm font-medium mb-1">
            Notas (opcional)
          </label>
          <textarea
            id="invoice_notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full rounded border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {error && (
          <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {successMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !invoiceUrl.trim()}
          className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700/90 disabled:bg-slate-300"
        >
          {submitting ? 'Subiendo...' : 'Asociar factura'}
        </button>
      </form>
    </section>
  );
}
